/**
 * Experiment page routes with code-splitting via React.lazy.
 *
 * Route configuration:
 *   /experiments          -> ExperimentLibrary (list view)
 *   /experiments/new      -> ExperimentBuilder (create form)
 *   /experiments/:id      -> ExperimentProgress (running view with summary card)
 *   /experiments/:id/results -> Results (heatmap, tornado, charts, compare)
 *
 * Each page component is lazy-loaded and wrapped in Suspense for bundle splitting.
 *
 * @module routes/experiments
 */

import React, { Suspense } from 'react';

// ---------------------------------------------------------------------------
// Loading fallback
// ---------------------------------------------------------------------------

function ExperimentLoadingFallback(): React.JSX.Element {
  return (
    <div
      className="flex items-center justify-center py-24"
      role="status"
      aria-label="Loading experiment page"
    >
      <div className="flex flex-col items-center gap-3">
        <div
          className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin motion-reduce:animate-none"
          aria-hidden="true"
        />
        <span className="text-sm text-slate-500 dark:text-slate-400">
          Loading...
        </span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Lazy page components
// ---------------------------------------------------------------------------

/**
 * ExperimentLibraryPage — List of saved experiments.
 * Wraps ExperimentLibrary component with page-level layout.
 */
const ExperimentLibraryPage = React.lazy(() =>
  import('./experimentPages').then((m) => ({ default: m.ExperimentLibraryPage })),
);

/**
 * ExperimentBuilderPage — Create a new experiment.
 * Wraps ExperimentBuilder component.
 */
const ExperimentBuilderPage = React.lazy(() =>
  import('./experimentPages').then((m) => ({ default: m.ExperimentBuilderPage })),
);

/**
 * ExperimentProgressPage — Monitor running experiment.
 * Shows ExperimentSummaryCard + progress indicators.
 */
const ExperimentProgressPage = React.lazy(() =>
  import('./experimentPages').then((m) => ({ default: m.ExperimentProgressPage })),
);

/**
 * ExperimentResultsPage — View experiment results.
 * Shows heatmap, tornado, charts, and comparison tools.
 */
const ExperimentResultsPage = React.lazy(() =>
  import('./experimentPages').then((m) => ({ default: m.ExperimentResultsPage })),
);

// ---------------------------------------------------------------------------
// Suspense wrappers
// ---------------------------------------------------------------------------

function LazyExperimentLibrary(): React.JSX.Element {
  return (
    <Suspense fallback={<ExperimentLoadingFallback />}>
      <ExperimentLibraryPage />
    </Suspense>
  );
}

function LazyExperimentBuilder(): React.JSX.Element {
  return (
    <Suspense fallback={<ExperimentLoadingFallback />}>
      <ExperimentBuilderPage />
    </Suspense>
  );
}

function LazyExperimentProgress(): React.JSX.Element {
  return (
    <Suspense fallback={<ExperimentLoadingFallback />}>
      <ExperimentProgressPage />
    </Suspense>
  );
}

function LazyExperimentResults(): React.JSX.Element {
  return (
    <Suspense fallback={<ExperimentLoadingFallback />}>
      <ExperimentResultsPage />
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Route config
// ---------------------------------------------------------------------------

export interface ExperimentRouteConfig {
  readonly path: string;
  readonly element: React.JSX.Element;
  readonly label: string;
}

/**
 * Route configuration array for experiment pages.
 * Import and spread into your main router configuration.
 *
 * Example:
 *   import { EXPERIMENT_ROUTES } from '@/routes/experiments';
 *   // In router config:
 *   ...EXPERIMENT_ROUTES.map(r => ({ path: r.path, element: r.element }))
 */
export const EXPERIMENT_ROUTES: ReadonlyArray<ExperimentRouteConfig> = [
  {
    path: '/experiments',
    element: <LazyExperimentLibrary />,
    label: 'Experiment Library',
  },
  {
    path: '/experiments/new',
    element: <LazyExperimentBuilder />,
    label: 'New Experiment',
  },
  {
    path: '/experiments/:id',
    element: <LazyExperimentProgress />,
    label: 'Experiment Progress',
  },
  {
    path: '/experiments/:id/results',
    element: <LazyExperimentResults />,
    label: 'Experiment Results',
  },
];

// Re-export page wrapper components for direct use
export {
  LazyExperimentLibrary,
  LazyExperimentBuilder,
  LazyExperimentProgress,
  LazyExperimentResults,
};
